import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Paper, CircularProgress, Snackbar, Alert,
  IconButton, FormControl, InputLabel, Select, MenuItem, Typography,
  Card, CardContent, Chip, Grid, Divider
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ScienceIcon from '@mui/icons-material/Science';
import PageHeader from '../components/PageHeader';
import DepartmentHeader from '../components/DepartmentHeader';
import { useDepartment } from '../context/DepartmentContext';
import { getSubjects, getTeachers, createSubject, getTeacherSubjects, createTeacherSubject, deleteTeacherSubject } from '../services/api';

const LabTeachers = () => {
  const { selectedDepartment } = useDepartment();
  const [labSubjects, setLabSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [teacherSubjects, setTeacherSubjects] = useState([]);
  const [currentMapping, setCurrentMapping] = useState({
    teacher_id: '',
    subject_id: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch subjects, teachers, and existing mappings when department changes
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedDepartment) return;
      
      try {
        setLoading(true);
        const [subjectsResponse, teachersResponse, mappingsResponse] = await Promise.all([
          getSubjects(),
          getTeachers(),
          getTeacherSubjects()
        ]);
        
        // Filter lab subjects by department
        const departmentLabSubjects = subjectsResponse.data.filter(
          subject => subject.department_id === selectedDepartment.department_id && subject.is_lab
        );
        
        // Filter teachers by department
        const departmentTeachers = teachersResponse.data.filter(
          teacher => teacher.department_id === selectedDepartment.department_id
        );
        
        // Filter mappings for lab subjects in this department
        const labSubjectIds = departmentLabSubjects.map(subject => subject.subject_id);
        const labMappings = mappingsResponse.data.filter(
          mapping => labSubjectIds.includes(mapping.subject_id)
        );
        
        setLabSubjects(departmentLabSubjects);
        setTeachers(departmentTeachers);
        setTeacherSubjects(labMappings);
      } catch (error) {
        console.error('Error fetching data:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load data',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [selectedDepartment]);

  const handleOpenDialog = () => {
    setCurrentMapping({
      teacher_id: '',
      subject_id: ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentMapping({
      ...currentMapping,
      [name]: value
    });
  };

  const handleSubmit = async () => {
    try {
      // Validate form
      if (!currentMapping.teacher_id || !currentMapping.subject_id) {
        setSnackbar({
          open: true,
          message: 'Please select both a teacher and a lab subject',
          severity: 'error'
        });
        return;
      }

      // Check if mapping already exists
      const existingMapping = teacherSubjects.find(
        ts => ts.teacher_id === parseInt(currentMapping.teacher_id) && 
              ts.subject_id === parseInt(currentMapping.subject_id)
      );

      if (existingMapping) {
        setSnackbar({
          open: true,
          message: 'This teacher is already assigned to this lab subject',
          severity: 'error'
        });
        return;
      }

      // Save new mapping
      const response = await createTeacherSubject({
        teacher_id: currentMapping.teacher_id,
        subject_id: currentMapping.subject_id
      });

      // Refresh data
      const mappingsResponse = await getTeacherSubjects();
      
      // Filter mappings for lab subjects in this department
      const labSubjectIds = labSubjects.map(subject => subject.subject_id);
      const labMappings = mappingsResponse.data.filter(
        mapping => labSubjectIds.includes(mapping.subject_id)
      );
      
      setTeacherSubjects(labMappings);

      setSnackbar({
        open: true,
        message: 'Lab teacher assignment successful',
        severity: 'success'
      });

      handleCloseDialog();
    } catch (error) {
      console.error('Error creating teacher-subject mapping:', error);
      setSnackbar({
        open: true,
        message: 'Failed to assign lab teacher',
        severity: 'error'
      });
    }
  };

  const handleDeleteMapping = async (teacherSubjectId) => {
    try {
      await deleteTeacherSubject(teacherSubjectId);
      
      // Refresh data
      const mappingsResponse = await getTeacherSubjects();
      
      // Filter mappings for lab subjects in this department
      const labSubjectIds = labSubjects.map(subject => subject.subject_id);
      const labMappings = mappingsResponse.data.filter(
        mapping => labSubjectIds.includes(mapping.subject_id)
      );
      
      setTeacherSubjects(labMappings);

      setSnackbar({
        open: true,
        message: 'Lab teacher assignment removed successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting teacher-subject mapping:', error);
      setSnackbar({
        open: true,
        message: 'Failed to remove lab teacher assignment',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({...snackbar, open: false});
  };

  // Get subject and teacher names for display
  const getSubjectName = (subjectId) => {
    const subject = labSubjects.find(s => s.subject_id === subjectId);
    return subject ? `${subject.subject_name} (${subject.subject_code})` : 'Unknown Subject';
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.teacher_id === teacherId);
    return teacher ? teacher.name : 'Unknown Teacher';
  };

  // Group teacher-subject mappings by subject
  const groupedBySubject = {};
  teacherSubjects.forEach(mapping => {
    if (!groupedBySubject[mapping.subject_id]) {
      groupedBySubject[mapping.subject_id] = [];
    }
    groupedBySubject[mapping.subject_id].push(mapping);
  });

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader title="Lab Teacher Assignment" />
      <DepartmentHeader />
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 2 }}>
        <Typography variant="h6" component="h2" sx={{ display: 'flex', alignItems: 'center' }}>
          <ScienceIcon sx={{ mr: 1 }} /> Lab Subjects and Teachers
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Assign Lab Teacher
        </Button>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : labSubjects.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>No lab subjects found for this department. Create lab subjects in the Subjects page first.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {labSubjects.map(subject => (
            <Grid item xs={12} key={subject.subject_id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      {subject.subject_name} ({subject.subject_code})
                      <Chip size="small" label="LAB" color="secondary" sx={{ ml: 1 }} />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Credits: {subject.credits} (2 Hours per Session)
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Assigned Lab Teachers:</Typography>
                  
                  {!groupedBySubject[subject.subject_id] || groupedBySubject[subject.subject_id].length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No teachers assigned to this lab yet</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {groupedBySubject[subject.subject_id].map(mapping => (
                        <Chip 
                          key={mapping.teacher_subject_id}
                          label={getTeacherName(mapping.teacher_id)}
                          onDelete={() => handleDeleteMapping(mapping.teacher_subject_id)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Dialog for assigning lab teachers */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Assign Lab Teacher</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, minWidth: 400 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Lab Subject</InputLabel>
              <Select
                name="subject_id"
                value={currentMapping.subject_id}
                label="Lab Subject"
                onChange={handleInputChange}
              >
                <MenuItem value="">
                  <em>Select a Lab Subject</em>
                </MenuItem>
                {labSubjects.map((subject) => (
                  <MenuItem key={subject.subject_id} value={subject.subject_id}>
                    {subject.subject_name} ({subject.subject_code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Teacher</InputLabel>
              <Select
                name="teacher_id"
                value={currentMapping.teacher_id}
                label="Teacher"
                onChange={handleInputChange}
              >
                <MenuItem value="">
                  <em>Select a Teacher</em>
                </MenuItem>
                {teachers.map((teacher) => (
                  <MenuItem key={teacher.teacher_id} value={teacher.teacher_id}>
                    {teacher.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Assign
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LabTeachers;
